import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  MapPin, Clock, Users, Landmark, Building2, Globe,
  Circle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LIKEKIND COMMUNITY CALENDAR V1
 *
 * Shared temporal coordination layer.
 * Aggregates activities + calendar events into one chronological view.
 *
 * This is NOT:
 * - A feed
 * - A discovery algorithm
 * - A promotional surface
 *
 * This IS:
 * - A planning surface
 * - A coordination tool
 * - A readable schedule
 */

const VIEW_MODES = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

const SCOPE_FILTERS = [
  { id: 'all', label: 'Community', icon: Globe },
  { id: 'personal', label: 'My Calendar', icon: CalendarIcon },
  { id: 'group', label: 'Groups', icon: Users },
  { id: 'commons', label: 'Commons', icon: Landmark },
  { id: 'institution', label: 'Institutions', icon: Building2 },
];

const QUICK_FILTERS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
];

// ── Helpers ──

function getStartOfView(date, view) {
  const d = new Date(date);
  if (view === 'day') {
    d.setHours(0, 0, 0, 0);
  } else if (view === 'week') {
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
  } else {
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
  }
  return d;
}

function getEndOfView(date, view) {
  const d = new Date(date);
  if (view === 'day') {
    d.setHours(23, 59, 59, 999);
  } else if (view === 'week') {
    d.setDate(d.getDate() + (6 - d.getDay()));
    d.setHours(23, 59, 59, 999);
  } else {
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    d.setHours(23, 59, 59, 999);
  }
  return d;
}

function formatDateRange(date, view) {
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (view === 'day') {
    return date.toLocaleDateString('en-US', { weekday: 'long', ...opts });
  }
  if (view === 'week') {
    const start = getStartOfView(date, 'week');
    const end = getEndOfView(date, 'week');
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', opts)}`;
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatDayHeader(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isToday(dateStr) {
  if (!dateStr) return false;
  return isSameDay(new Date(dateStr), new Date());
}

// Group entries by date
function groupByDate(entries) {
  const groups = {};
  for (const entry of entries) {
    const dateKey = entry.start_time ? new Date(entry.start_time).toDateString() : 'Unknown';
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(entry);
  }
  return Object.entries(groups).map(([dateKey, items]) => ({
    dateKey,
    date: items[0]?.start_time || '',
    items,
  }));
}

function sourceColor(sourceType) {
  switch (sourceType) {
    case 'group': return 'var(--lk-primary-green)';
    case 'commons': return 'var(--lk-earth-brown)';
    case 'institution': return 'var(--lk-secondary-orange)';
    case 'personal': return 'var(--lk-sage-light)';
    default: return 'var(--lk-soft-stone)';
  }
}

function sourceIcon(sourceType) {
  switch (sourceType) {
    case 'group': return <Users className="w-3 h-3" />;
    case 'commons': return <Landmark className="w-3 h-3" />;
    case 'institution': return <Building2 className="w-3 h-3" />;
    case 'personal': return <CalendarIcon className="w-3 h-3" />;
    default: return <Globe className="w-3 h-3" />;
  }
}

// ── Entry Row ──

function CalendarEntry({ entry, onClick }) {
  const color = sourceColor(entry.source_type);

  return (
    <button
      onClick={onClick}
      className="w-full text-left group"
      data-testid={`calendar-entry-${entry.entry_id}`}
      style={{ outline: 'none' }}
    >
      <div
        className="flex items-start gap-3 py-3 px-3 rounded-lg transition-colors"
        style={{
          borderLeft: `3px solid ${color}`,
          backgroundColor: 'transparent',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--fy-surface-muted)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        {/* Time column */}
        <div className="flex-shrink-0 w-16 pt-0.5">
          <span className="text-xs font-medium tabular-nums" style={{ color: 'var(--fy-text-secondary)' }}>
            {formatTime(entry.start_time)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight truncate" style={{ color: 'var(--fy-text-primary)' }}>
            {entry.title}
          </p>

          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {/* Source badge */}
            {entry.source_label && (
              <span className="inline-flex items-center gap-1 text-xs" style={{ color }}>
                {sourceIcon(entry.source_type)}
                <span className="truncate max-w-[120px]">{entry.source_label}</span>
              </span>
            )}

            {/* Location */}
            {entry.location && (
              <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                <MapPin className="w-3 h-3" />
                <span className="truncate max-w-[140px]">{entry.location}</span>
              </span>
            )}

            {/* Participant count */}
            {entry.participant_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                <Users className="w-3 h-3" />
                {entry.participant_count}
              </span>
            )}
          </div>
        </div>

        {/* Lifecycle state (quiet) */}
        {entry.lifecycle_state && entry.lifecycle_state !== 'completed' && (
          <div className="flex-shrink-0">
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: entry.lifecycle_state === 'active'
                  ? 'rgba(122, 143, 91, 0.12)'
                  : entry.lifecycle_state === 'confirmed'
                    ? 'rgba(217, 138, 58, 0.10)'
                    : 'rgba(185, 179, 170, 0.15)',
                color: entry.lifecycle_state === 'active'
                  ? 'var(--lk-primary-green)'
                  : entry.lifecycle_state === 'confirmed'
                    ? 'var(--lk-secondary-orange)'
                    : 'var(--fy-text-disabled)',
              }}
            >
              <Circle className="w-2 h-2" style={{ fill: 'currentColor' }} />
              {entry.lifecycle_state}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

// ── Day Group ──

function DayGroup({ dateStr, items, onEntryClick }) {
  const today = isToday(dateStr);

  return (
    <div className="mb-1" data-testid={`day-group-${new Date(dateStr).toISOString().slice(0, 10)}`}>
      {/* Day header */}
      <div
        className="sticky top-14 z-10 flex items-center gap-2 px-3 py-2"
        style={{
          backgroundColor: 'var(--fy-background-primary)',
          borderBottom: '1px solid var(--fy-border-subtle)',
        }}
      >
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${today ? '' : ''}`}
          style={{ color: today ? 'var(--lk-primary-green)' : 'var(--fy-text-secondary)' }}
        >
          {today ? 'Today' : formatDayHeader(dateStr)}
        </span>
        {today && (
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: 'var(--lk-primary-green)' }}
          />
        )}
        <span className="text-xs ml-auto" style={{ color: 'var(--fy-text-disabled)' }}>
          {items.length} {items.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Entries */}
      <div>
        {items.map(entry => (
          <CalendarEntry
            key={entry.entry_id}
            entry={entry}
            onClick={() => onEntryClick(entry)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main Component ──

export default function CommunityCalendar() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  const initialScope = searchParams.get('scope') || 'all';
  const initialScopeId = searchParams.get('scope_id') || '';

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [scopeFilter, setScopeFilter] = useState(initialScope);
  const [scopeId] = useState(initialScopeId);
  const [currentDate, setCurrentDate] = useState(new Date());

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const startDate = getStartOfView(currentDate, viewMode);
      const endDate = getEndOfView(currentDate, viewMode);

      const params = new URLSearchParams({
        scope: scopeFilter,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
      if (scopeId) params.set('scope_id', scopeId);

      const res = await axios.get(`${API}/api/community-calendar?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setEntries(res.data.entries || []);
    } catch (err) {
      if (err.response?.status === 401 && scopeFilter === 'personal') {
        toast.error('Sign in to view your personal calendar');
      } else {
        console.error('Failed to load community calendar:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, scopeFilter, scopeId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Navigation
  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + direction);
    else if (viewMode === 'week') d.setDate(d.getDate() + direction * 7);
    else d.setMonth(d.getMonth() + direction);
    setCurrentDate(d);
  };

  const jumpToToday = () => setCurrentDate(new Date());

  const handleQuickFilter = (filterId) => {
    const now = new Date();
    setCurrentDate(now);
    if (filterId === 'today') setViewMode('day');
    else if (filterId === 'week') setViewMode('week');
    else if (filterId === 'month') setViewMode('month');
  };

  const handleScopeChange = (newScope) => {
    setScopeFilter(newScope);
    const params = new URLSearchParams(searchParams);
    params.set('scope', newScope);
    if (newScope !== initialScope) params.delete('scope_id');
    setSearchParams(params, { replace: true });
  };

  const handleEntryClick = (entry) => {
    if (entry.route_target) navigate(entry.route_target);
  };

  const dayGroups = groupByDate(entries);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* ── Header ── */}
      <header
        className="sticky top-0 z-50"
        style={{
          backgroundColor: 'var(--fy-background-primary)',
          borderBottom: '1px solid var(--fy-border-subtle)',
        }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate(-1)}
                variant="ghost"
                size="icon"
                data-testid="community-calendar-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-semibold text-base" style={{ color: 'var(--fy-text-primary)' }}>
                Community Calendar
              </h1>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={jumpToToday}
                className="text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                style={{ color: 'var(--lk-primary-green)' }}
                data-testid="jump-to-today"
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--fy-surface-muted)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Today
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-3 pb-16">
        {/* ── Scope Filters ── */}
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="scope-filters">
          {SCOPE_FILTERS.map(f => {
            const active = scopeFilter === f.id;
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => handleScopeChange(f.id)}
                className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors"
                style={{
                  backgroundColor: active ? 'var(--lk-primary-green)' : 'var(--fy-surface-muted)',
                  color: active ? '#fff' : 'var(--fy-text-secondary)',
                }}
                data-testid={`scope-${f.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {f.label}
              </button>
            );
          })}
        </div>

        {/* ── Date Navigation + View Mode ── */}
        <div
          className="flex items-center justify-between mb-3 px-1"
          data-testid="date-navigation"
        >
          <Button
            onClick={() => navigateDate(-1)}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            data-testid="nav-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <div className="text-center flex-1">
            <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
              {formatDateRange(currentDate, viewMode)}
            </p>
            {/* View mode pills */}
            <div className="flex gap-1 mt-1.5 justify-center">
              {VIEW_MODES.map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className="px-2.5 py-0.5 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: viewMode === mode.id ? 'var(--lk-primary-green)' : 'transparent',
                    color: viewMode === mode.id ? '#fff' : 'var(--fy-text-disabled)',
                  }}
                  data-testid={`view-${mode.id}`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={() => navigateDate(1)}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            data-testid="nav-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* ── Quick Filters ── */}
        <div className="flex gap-2 mb-4" data-testid="quick-filters">
          {QUICK_FILTERS.map(qf => (
            <button
              key={qf.id}
              onClick={() => handleQuickFilter(qf.id)}
              className="px-3 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                border: '1px solid var(--fy-border-subtle)',
                color: 'var(--fy-text-secondary)',
                backgroundColor: 'transparent',
              }}
              data-testid={`quick-${qf.id}`}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'var(--fy-surface-muted)';
                e.currentTarget.style.borderColor = 'var(--lk-primary-green)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'var(--fy-border-subtle)';
              }}
            >
              {qf.label}
            </button>
          ))}
        </div>

        {/* ── Calendar Body ── */}
        {loading ? (
          <div className="text-center py-16" data-testid="loading">
            <Clock
              className="w-8 h-8 mx-auto mb-3 animate-pulse"
              style={{ color: 'var(--fy-text-disabled)' }}
            />
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              Loading schedule...
            </p>
          </div>
        ) : entries.length === 0 ? (
          <Card
            className="p-10 text-center"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="empty-state"
          >
            <CalendarIcon
              className="w-10 h-10 mx-auto mb-3"
              style={{ color: 'var(--fy-text-disabled)' }}
            />
            <p className="font-medium text-sm mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Nothing scheduled
            </p>
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              {scopeFilter === 'personal'
                ? 'Activities you join or events you create will appear here.'
                : 'No community activities or events in this time range.'}
            </p>
          </Card>
        ) : (
          <div data-testid="calendar-entries">
            {dayGroups.map(group => (
              <DayGroup
                key={group.dateKey}
                dateStr={group.date}
                items={group.items}
                onEntryClick={handleEntryClick}
              />
            ))}

            {/* Entry count footer */}
            <div className="text-center mt-6 pb-4">
              <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · Chronological order
              </span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
